import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { SelectField } from '@/components/SelectField';
import { usePostWonTitleMutation } from '@/services/queries/won-title';
import { PostTeamWonTitleRequest } from '@/services/request/won-title';
import { Competition } from '@/model';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface WonTitleCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  competitions: Competition[] | undefined;
  onClose: () => void;
  onDone?: () => void;
}

export const WonTitleCreateModal = ({ isOpen, competitions, onClose, onDone }: WonTitleCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostWonTitleMutation();

  competitions?.sort((a, b) => a.name.localeCompare(b.name));

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      year: undefined,
      competition_uuid: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostTeamWonTitleRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        year: data.year,
        competition_uuid: data.competition_uuid,
      };
      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_won_title.components.won_title_create_modal.won_title_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) || t('pages.application_won_title.components.won_title_create_modal.won_title_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_won_title.components.won_title_create_modal.won_title_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_won_title.components.won_title_create_modal.won_title_create_team_label')}
              placeholder={t('pages.application_won_title.components.won_title_create_modal.won_title_create_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <Flex direction='row' gap={6} width='100%'>
              <InputGroup display='flex' flexDirection='column' flex='1'>
                <InputField
                  withoutFloating
                  label={t('pages.application_won_title.components.won_title_create_modal.won_title_create_year_label')}
                  {...register('year')}
                  maxLength={4}
                  rootProps={{ isInvalid: !!errors?.year, errorMessage: errors?.year?.message }}
                  onChange={e => {
                    const input = e.target as HTMLInputElement;
                    const value = input.value;

                    // Permitir apenas 4 dígitos numéricos
                    if (/^\d{0,4}$/.test(value)) {
                      input.value = value; // Aceitar somente valores válidos
                    } else {
                      input.value = value.replace(/\D/g, '').slice(0, 4); // Corrigir valores inválidos
                    }
                  }}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column' flex='1'>
                <Text fontWeight='medium'>
                  {t('pages.application_won_title.components.won_title_create_modal.won_title_create_competition_label')}
                </Text>
                <SelectField
                  defaultValue=''
                  {...register('competition_uuid')}
                  rootProps={{ isInvalid: !!errors?.competition_uuid, errorMessage: errors?.competition_uuid?.message, mt: 0 }}
                >
                  <option value='' disabled>
                    {t(
                      'pages.application_won_title.components.won_title_create_modal.won_title_create_competition_default_option',
                    )}
                  </option>
                  {competitions?.map(item => (
                    <option key={item.uuid} value={item.uuid}>
                      {item.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_won_title.components.won_title_create_modal.won_title_create_cancel_button')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_won_title.components.won_title_create_modal.won_title_create_submit_button')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
